import utils from '@/js/utils'

const singleMuPerimeter = 1000
const singleMuArea = 1000000 // single-sided
// cutMu factors
const cutEdgeMuFactor = 1.5 / 4 // 1.5 Mu = 180s or 3mins per 4000mm so divided by 4 to get linear metre
const compartmentMuFactor = 2 // detail cut for each compartment
const topBaseMuFactor = 0.5 // detail cut for top and base
const sideMuFactor = 1 // detail cut for each side
const doorGrooveMuFactor = 1.7 * 2 // detail cut for each door groove (for sides)
const singleDoorMuFactor = 0.4 // detail cut for each single door
const lightingCutMuFactor = 1.9 + 2.5 // detail cut for 1set of lighting + cable cover

// finishMu factors
const laminateEdgeMuFactor = 2.46
const veneerEdgeMuFactor = 1
const veneerSingleSurfaceMuFactor = 1.075 //half of double surface = 2.15 / 2

// assembly and pack mu factors
const assemblyAndPackMuFactorUrtil = 27.5 // roughly 27.5 * 120s = 3300s = 55min

export default {
  get(urtils, materials, taskType) {
    let mu = 0
    if (!urtils) return mu

    for (let urtil of urtils) {
      if (
        !urtil.type ||
        !urtil.carcass ||
        (urtil.type == 's' && !urtil.material1) ||
        (urtil.type == 't' && (!urtil.material1 || !urtil.material2))
      ) {
        continue
      }

      // Calculate perimeter and surface once per urtil (for finish taskType)
      if (taskType == 'finish') {
        let urtilPerimeter = 0
        let urtilSurface = 0
        let hasAnyGrain = false

        // Calculate total perimeter and surface for finish taskType, and check for grain
        materials.forEach((material) => {
          if (!material.value || !material.thickness || !material.w || !material.h) return

          // Carcass
          if (material.id === urtil.carcass) {
            urtilPerimeter += 2 * 2 * (urtil.w + urtil.d) + 2 * 2 * (urtil.h + urtil.d)

            // shelves
            if (urtil.shelving) {
              const shelfPerimeter =
                urtil.shelving === 'fixed' ? urtil.w / urtil.compartments : (urtil.w / urtil.compartments + urtil.d) * 2
              const numShelves = urtil.shelvingOptions.qty
              urtilPerimeter += shelfPerimeter * urtil.compartments * numShelves
            }

            // fins
            if (urtil.compartments > 1) {
              const finPerimeter = urtil.h
              const numFins = urtil.compartments - 1
              urtilPerimeter += finPerimeter * numFins
            }

            if (material.hasGrain) {
              hasAnyGrain = true
              urtilSurface += (2 * urtil.d * urtil.h + 2 * urtil.d * urtil.w) * 2 // inside and out for 2 sides and 2 bases
              if (urtil.shelving) {
                const numShelves = urtil.shelvingOptions.qty
                urtilSurface += urtil.d * (urtil.w / urtil.compartments) * 2 * numShelves
              }
              if (urtil.compartments > 1) {
                const numFins = urtil.compartments - 1
                urtilSurface += urtil.d * urtil.h * 2 * numFins
              }
            }
          }

          // Back
          if (material.id === urtil.back) {
            if (material.hasGrain) {
              hasAnyGrain = true
              urtilSurface += urtil.w * urtil.h
            }
          }

          // Single Door
          if (urtil.type !== 'o' && urtil.material1 && material.id === urtil.material1) {
            urtilPerimeter += (urtil.w / 2 + urtil.h) * 2
            if (material.hasGrain) {
              hasAnyGrain = true
              urtilSurface += (urtil.w / 2) * urtil.h * 2
            }
          }

          // Double Door (as second door)
          if (urtil.type === 't' && urtil.material2 && material.id === urtil.material2) {
            urtilPerimeter += (urtil.w / 2 + urtil.h) * 2
            if (material.hasGrain) {
              hasAnyGrain = true
              urtilSurface += (urtil.w / 2) * urtil.h * 2
            }
          }
        })

        // Apply finish MU once per urtil using total perimeter/surface
        const edgeMu = hasAnyGrain
          ? (urtilPerimeter / singleMuPerimeter) * veneerEdgeMuFactor
          : (urtilPerimeter / singleMuPerimeter) * laminateEdgeMuFactor
        const surfaceMu = urtilSurface > 0 ? (urtilSurface / singleMuArea) * veneerSingleSurfaceMuFactor : 0
        mu += edgeMu + surfaceMu
      }

      // Second pass: handle cut taskType per material
      if (taskType == 'cut') {
        materials.forEach((material) => {
          if (!material.value || !material.thickness || !material.w || !material.h) return

          let materialPerimeter = 0
          let cutMu = 0

          if (material.id === urtil.carcass) {
            materialPerimeter += 2 * 2 * (urtil.w + urtil.d) + 2 * 2 * (urtil.h + urtil.d)

            // shelves
            if (urtil.shelving) {
              const shelfPerimeter = (urtil.w / urtil.compartments + urtil.d) * 2
              const numShelves = urtil.shelvingOptions.qty
              materialPerimeter += shelfPerimeter * urtil.compartments * numShelves
            }

            // fins
            if (urtil.compartments > 1) {
              const finPerimeter = (urtil.h + urtil.d) * 2
              const numFins = urtil.compartments - 1
              materialPerimeter += finPerimeter * numFins
            }

            // total edges
            cutMu += (materialPerimeter / singleMuPerimeter) * cutEdgeMuFactor
            // top and base
            cutMu += 2 * topBaseMuFactor
            // sides
            cutMu += 2 * sideMuFactor
            // compartments
            cutMu += (urtil.compartments - 1) * compartmentMuFactor
            // shelves
            cutMu += urtil.shelving ? urtil.shelvingOptions.qty * compartmentMuFactor : 0
            // lighting
            if (urtil.lighting) {
              cutMu += lightingCutMuFactor
            }
          }

          if (material.id === urtil.back) {
            materialPerimeter += (urtil.w + urtil.h) * 2
            // back
            cutMu += (materialPerimeter / singleMuPerimeter) * cutEdgeMuFactor
          }

          if (urtil.type !== 'o' && urtil.material1 && material.id === urtil.material1) {
            // first door
            cutMu += singleDoorMuFactor + doorGrooveMuFactor
          }

          if (urtil.type === 't' && urtil.material2 && material.id === urtil.material2) {
            // second door
            cutMu += singleDoorMuFactor + doorGrooveMuFactor
          }

          // add to total mu
          mu += cutMu
        })
      }
      if (taskType == 'assemble') {
        let assemblyMu = assemblyAndPackMuFactorUrtil
        if (urtil.preAssembled && !urtil.trimming) assemblyMu *= 0.7
        if (!urtil.preAssembled && urtil.trimming) assemblyMu *= 0.3
        mu += assemblyMu
      }
    }

    return utils.toFixedNumber(mu, 2)
  }
}
